// [US 6.1] Contexte d'authentification Sentinel — session restaurée au chargement
// via le refresh token (cookie httpOnly web / stockage natif Android).
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authClient, AuthUser } from '@/services/authClient';

interface AuthContextValue {
    user: AuthUser | null;
    loading: boolean;
    refreshUser: () => Promise<void>;
    setUser: (user: AuthUser | null) => void;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    refreshUser: async () => undefined,
    setUser: () => undefined,
    logout: async () => undefined,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        const me = await authClient.getMe();
        setUser(me);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (await authClient.tryRefresh()) {
                const me = await authClient.getMe();
                if (!cancelled) setUser(me);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const logout = useCallback(async () => {
        await authClient.logout();
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, refreshUser, setUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
